import React, { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import MaterialTable, { Column } from "material-table";
import BreadcrumbNav from "../../../../components/BreadcrumbNav/BreadcrumbNav";
import CustomButton from "../../../../components/Button/CustomButton";
import DeleteConfirmationDialog from "../../../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { materialTableES } from "../../../../services/materialTableES";
import { errorToast, successToast } from "../../../../services/toasts";
import {
  useCreateRbacRole,
  useDeleteRbacRole,
  useGetRbacPermissions,
  useGetRbacRoles,
  useSetRbacRolePermissions,
  useUpdateRbacRole,
} from "../../../../utils/react-query/queryRbac/queries";
import {
  SupplierRbacPermission,
  SupplierRbacRole,
  SupplierRoleFormValues,
} from "../../types";
import styles from "./RolesManagement.module.scss";

const INITIAL_ROLE_FORM: SupplierRoleFormValues = {
  name: "",
  description: "",
  permission_ids: [],
};

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  return error instanceof Error ? error.message : fallbackMessage;
};

const RolesManagement = () => {
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<SupplierRbacRole | null>(
    null
  );
  const [roleForm, setRoleForm] =
    useState<SupplierRoleFormValues>(INITIAL_ROLE_FORM);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: () => {},
  });

  const { data: roles = [], isLoading: isLoadingRoles } = useGetRbacRoles();
  const {
    data: permissions = [],
    isLoading: isLoadingPermissions,
    isError: hasPermissionsError,
    error: permissionsError,
  } = useGetRbacPermissions();
  const { mutateAsync: createRole, isLoading: isCreatingRole } =
    useCreateRbacRole();
  const { mutateAsync: updateRole, isLoading: isUpdatingRole } =
    useUpdateRbacRole();
  const { mutateAsync: deleteRole } = useDeleteRbacRole();
  const { mutateAsync: setRolePermissions, isLoading: isSavingPermissions } =
    useSetRbacRolePermissions();

  const summary = useMemo(() => {
    const systemRoles = roles.filter((role) => role.is_system).length;

    return {
      totalRoles: roles.length,
      systemRoles,
      customRoles: roles.length - systemRoles,
    };
  }, [roles]);

  const permissionsByModule = useMemo<Record<string, SupplierRbacPermission[]>>(
    () =>
      permissions.reduce<Record<string, SupplierRbacPermission[]>>(
        (acc, permission) => {
          if (!acc[permission.module]) {
            acc[permission.module] = [];
          }

          acc[permission.module].push(permission);
          return acc;
        },
        {}
      ),
    [permissions]
  );

  const permissionsUnavailable =
    !isLoadingPermissions && !hasPermissionsError && permissions.length === 0;
  const canSubmitRole =
    !isLoadingPermissions &&
    !hasPermissionsError &&
    !permissionsUnavailable &&
    !isCreatingRole &&
    !isUpdatingRole &&
    !isSavingPermissions;

  const openCreateDialog = () => {
    setSelectedRole(null);
    setRoleForm(INITIAL_ROLE_FORM);
    setIsRoleDialogOpen(true);
  };

  const openEditDialog = (role: SupplierRbacRole) => {
    setSelectedRole(role);
    setRoleForm({
      name: role.name,
      description: role.description || "",
      permission_ids: role.permissions.map((permission) => permission.id),
    });
    setIsRoleDialogOpen(true);
  };

  const closeRoleDialog = () => {
    setSelectedRole(null);
    setRoleForm(INITIAL_ROLE_FORM);
    setIsRoleDialogOpen(false);
  };

  const togglePermission = (permissionId: number) => {
    setRoleForm((prev) => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(permissionId)
        ? prev.permission_ids.filter(
            (currentPermissionId) => currentPermissionId !== permissionId
          )
        : [...prev.permission_ids, permissionId],
    }));
  };

  const handleSaveRole = async () => {
    if (isLoadingPermissions || hasPermissionsError || permissionsUnavailable) {
      return errorToast(
        hasPermissionsError
          ? getErrorMessage(
              permissionsError,
              "No fue posible cargar los permisos del backend"
            )
          : permissionsUnavailable
          ? "No hay permisos disponibles para asignar a este rol"
          : "Espera a que se carguen los permisos antes de guardar el rol"
      );
    }

    if (!selectedRole && !roleForm.name.trim()) {
      return errorToast("El nombre del rol es obligatorio");
    }

    try {
      let roleId = selectedRole?.id;

      if (selectedRole) {
        await updateRole({
          roleId: selectedRole.id,
          data: {
            name: roleForm.name.trim(),
            description: roleForm.description.trim(),
          },
        });
      } else {
        const createdRole = await createRole({
          name: roleForm.name.trim(),
          description: roleForm.description.trim(),
        });
        roleId = createdRole.id;
      }

      if (roleId) {
        await setRolePermissions({
          roleId,
          permission_ids: roleForm.permission_ids,
        });
      }

      successToast(
        selectedRole ? "Rol actualizado con éxito" : "Rol creado con éxito"
      );
      closeRoleDialog();
    } catch (error) {
      errorToast(getErrorMessage(error, "No fue posible guardar el rol"));
    }
  };

  const handleDeleteRole = async (roleId: number) => {
    try {
      await deleteRole(roleId);
      successToast("Rol eliminado con éxito");
    } catch (error) {
      errorToast(getErrorMessage(error, "No fue posible eliminar el rol"));
    }
  };

  const renderPermissionsSelector = () => {
    if (isLoadingPermissions) {
      return (
        <Paper className={styles.emptyState} elevation={0}>
          <Typography className={styles.primaryText}>
            Cargando permisos desde el backend...
          </Typography>
          <Typography className={styles.secondaryText}>
            Espera a que termine la consulta para configurar el rol.
          </Typography>
        </Paper>
      );
    }

    if (hasPermissionsError) {
      return (
        <Paper className={styles.emptyState} elevation={0}>
          <Typography className={styles.primaryText}>
            No fue posible cargar los permisos
          </Typography>
          <Typography className={styles.secondaryText}>
            {getErrorMessage(
              permissionsError,
              "Verifica la respuesta de /v2/rbac/permissions e inténtalo nuevamente."
            )}
          </Typography>
        </Paper>
      );
    }

    if (permissionsUnavailable) {
      return (
        <Paper className={styles.emptyState} elevation={0}>
          <Typography className={styles.primaryText}>
            Aún no hay permisos disponibles
          </Typography>
          <Typography className={styles.secondaryText}>
            El backend no devolvió permisos para este proveedor.
          </Typography>
        </Paper>
      );
    }

    return (
      <Grid container spacing={2}>
        {Object.entries(permissionsByModule).map(
          ([module, modulePermissions]) => (
            <Grid item xs={12} md={6} key={module}>
              <Paper className={styles.permissionCard} elevation={0}>
                <Typography className={styles.moduleTitle}>{module}</Typography>
                <div className={styles.permissionsList}>
                  {modulePermissions.map((permission) => (
                    <FormControlLabel
                      key={permission.id}
                      control={
                        <Checkbox
                          color="primary"
                          checked={roleForm.permission_ids.includes(
                            permission.id
                          )}
                          onChange={() => togglePermission(permission.id)}
                        />
                      }
                      label={
                        <div>
                          <Typography className={styles.primaryText}>
                            {permission.action}
                          </Typography>
                          <Typography className={styles.secondaryText}>
                            {permission.description}
                          </Typography>
                        </div>
                      }
                    />
                  ))}
                </div>
              </Paper>
            </Grid>
          )
        )}
      </Grid>
    );
  };

  const columns: Column<SupplierRbacRole>[] = [
    {
      title: "Rol",
      field: "name",
      render: (rowData) => (
        <div>
          <Typography className={styles.primaryText}>{rowData.name}</Typography>
          <Typography className={styles.secondaryText}>
            {rowData.description || "Sin descripción"}
          </Typography>
        </div>
      ),
    },
    {
      title: "Tipo",
      field: "is_system",
      filtering: false,
      render: (rowData) => (
        <span
          className={`${styles.roleTypeChip} ${
            rowData.is_system ? styles.systemRoleTypeChip : ""
          }`}
        >
          <span
            className={`${styles.roleTypeText} ${
              rowData.is_system ? styles.systemRoleTypeText : ""
            }`}
          >
            {rowData.is_system ? "Sistema" : "Personalizado"}
          </span>
        </span>
      ),
    },
    {
      title: "Permisos",
      field: "permissions",
      filtering: false,
      render: (rowData) => `${rowData.permissions?.length || 0} permisos`,
    },
    {
      title: "Acciones",
      field: "actions",
      sorting: false,
      filtering: false,
      render: (rowData) => (
        <div className={styles.rowActions}>
          <Tooltip title="Editar rol">
            <IconButton onClick={() => openEditDialog(rowData)}>
              <EditIcon className={styles.actionIconAccent} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              rowData.is_system
                ? "Los roles del sistema no se pueden eliminar"
                : "Eliminar rol"
            }
          >
            <span>
              <IconButton
                disabled={rowData.is_system}
                onClick={() =>
                  setConfirmDialog({
                    isOpen: true,
                    title: "Eliminar rol",
                    subTitle:
                      "Solo se eliminará el rol personalizado. ¿Deseas continuar?",
                    onConfirm: async () => {
                      await handleDeleteRole(rowData.id);
                      setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
                    },
                  })
                }
              >
                <DeleteIcon
                  className={
                    rowData.is_system
                      ? styles.actionIconDisabled
                      : styles.actionIconDanger
                  }
                />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <section className={`layout-dashboard-container ${styles.page}`}>
      <div className="header-container">
        <Box className={styles.headerRow}>
          <div>
            <h4 className="header-title">Roles y permisos</h4>
            <BreadcrumbNav
              firstText="Proveedor"
              secondText="Roles y permisos"
              secondLink="/proveedor/roles-permisos"
              thirdText="Gestión"
            />
          </div>
        </Box>
      </div>

      <DeleteConfirmationDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />

      <Grid container spacing={2} className={styles.summaryGrid}>
        {[
          { label: "Total roles", value: summary.totalRoles },
          { label: "Roles del sistema", value: summary.systemRoles },
          { label: "Roles personalizados", value: summary.customRoles },
        ].map((item) => (
          <Grid item xs={12} sm={4} key={item.label}>
            <Paper className={styles.summaryCard} elevation={0}>
              <Typography className={styles.summaryLabel}>
                {item.label}
              </Typography>
              <Typography className={styles.summaryValue}>
                {item.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper className={styles.helperCard} elevation={0}>
        <Typography className={styles.primaryText}>
          Define aquí los permisos internos del proveedor.
        </Typography>
        <Typography className={styles.secondaryText}>
          Los roles del sistema se pueden ajustar en permisos y descripción,
          pero no se eliminan.
        </Typography>
      </Paper>

      <div className={styles.actionBar}>
        <CustomButton
          color="secondary"
          shape="little-rounded"
          icon={AddCircleOutlineIcon}
          onClick={openCreateDialog}
        >
          Crear rol
        </CustomButton>
      </div>

      <MaterialTable
        title=""
        columns={columns}
        data={roles}
        isLoading={isLoadingRoles || isLoadingPermissions}
        localization={materialTableES}
        options={{
          search: true,
          filtering: false,
          searchFieldVariant: "outlined",
        }}
      />

      <Dialog
        open={isRoleDialogOpen}
        onClose={closeRoleDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{ className: styles.dialogPaper }}
      >
        <DialogTitle>
          {selectedRole ? `Editar rol: ${selectedRole.name}` : "Crear rol"}
        </DialogTitle>
        <DialogContent dividers className={styles.dialogContent}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre del rol"
                variant="outlined"
                value={roleForm.name}
                disabled={selectedRole?.is_system}
                onChange={(event) =>
                  setRoleForm((prev) => ({ ...prev, name: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Descripción"
                variant="outlined"
                value={roleForm.description}
                onChange={(event) =>
                  setRoleForm((prev) => ({
                    ...prev,
                    description: event.target.value,
                  }))
                }
              />
            </Grid>
          </Grid>

          <Box className={styles.permissionSection}>
            <Typography className={styles.primaryText}>Permisos</Typography>
            <Typography className={styles.secondaryText}>
              Selecciona los permisos que heredará este rol.
            </Typography>
          </Box>

          {renderPermissionsSelector()}
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <CustomButton
            color="outlined"
            shape="little-rounded"
            onClick={closeRoleDialog}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            color="secondary"
            shape="little-rounded"
            onClick={handleSaveRole}
            disabled={!canSubmitRole}
            isLoading={isCreatingRole || isUpdatingRole || isSavingPermissions}
          >
            Guardar rol
          </CustomButton>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default RolesManagement;
